import { useCallback, useEffect, useState } from "react";
import type { DesktopPrefs, DesktopPrefsPatch } from "@/types/bridge";

/**
 * Mac-local preferences from the Electron shell (null in a browser or an older shell). Follows
 * changes made from the native menu.
 */
export function useDesktopPrefs(): [DesktopPrefs | null, (patch: DesktopPrefsPatch) => Promise<DesktopPrefs>] {
  const bridge = window.deskBridge;
  const [prefs, setPrefs] = useState<DesktopPrefs | null>(null);
  useEffect(() => {
    if (!bridge?.getDesktopPrefs) return;
    let live = true;
    void bridge.getDesktopPrefs().then((p) => live && setPrefs(p));
    const off = bridge.onDesktopPrefs?.((p) => setPrefs(p));
    return () => {
      live = false;
      off?.();
    };
  }, [bridge]);
  const update = useCallback(
    async (patch: DesktopPrefsPatch) => {
      if (!bridge?.setDesktopPrefs) throw new Error("Not available in this version of Desk");
      const next = await bridge.setDesktopPrefs(patch);
      setPrefs(next);
      return next;
    },
    [bridge],
  );
  return [prefs, update];
}
